import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import { useAuth } from '../context/AuthContext';
import { hasNavPermission } from '../components/ProtectedRoute';
import { exportSaleToExcel, exportSaleToPDF, salesSheet } from '../utils/salesSheetExport';
import ExportPreviewModal from '../components/ExportPreviewModal';
import RowProperties from '../components/RowProperties';
import SheetPreview from '../components/SheetPreview';
import { isoYearOfDate, useHistoryFilters } from '../components/HistoryFilters';
import HistorySummary from '../components/HistorySummary';
import { fmtMoney, isMissingPrice, summarizeSales } from '../utils/salesSummary';
import './CompaniesPage.css';
import './SalesFormPage.css';
import '../styles/rowActions.css';
import '../styles/priceAlert.css';
import { SALE_PRICES_CHANGED_EVENT } from '../components/Navbar';
import '../styles/topbarButtons.css';
import '../styles/pageHeader.css';

// How the shared Historial filters read a sale.
const FILTER_FIELDS = {
  year: (s) => isoYearOfDate(s.date),
  week: (s) => s.week,
  farm: (s) => s.farm_name,
  exporters: (s) => [s.exporter_name],
};

function formatDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d}/${m}/${y}`;
}

// "6.123400" from the API -> "6,1234" for display/editing.
function formatPrice(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(Number(value)).replace('.', ',');
}

// Summary cards (formulas in utils/salesSummary.js, shared with Resumen de Ventas).
function salesSummary(rows, withPrices) {
  const t = summarizeSales(rows);
  return {
    real: [
      ['Total cajas', t.totalBoxes],
      ...(withPrices ? [['Total real', fmtMoney(t.totalReal)], ['Precio real promedio', fmtMoney(t.avgReal)]] : []),
    ],
    boxes43: [
      ['Total cajas 43lbs', Math.round(t.totalBoxes43)],
      ...(withPrices ? [['Total 43lbs', fmtMoney(t.total43)], ['Precio 43lbs promedio', fmtMoney(t.avg43)]] : []),
    ],
  };
}

// Matches the model: DecimalField(max_digits=12, decimal_places=6).
const PRICE_PATTERN = /^\d{1,6}(\.\d{1,6})?$/;


function PriceCell({ sale, onSaved }) {
  const [draft, setDraft] = useState(formatPrice(sale.price));
  const [state, setState] = useState(null); // 'saving' | 'saved' | 'error'
  const cancelledRef = useRef(false);

  useEffect(() => { setDraft(formatPrice(sale.price)); }, [sale.price]);

  const save = async () => {
    // Escape blurs the input too; skip the save that blur would trigger.
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const normalized = draft.trim().replace(',', '.');
    if (normalized === formatPrice(sale.price).replace(',', '.')) return;
    if (normalized !== '' && !PRICE_PATTERN.test(normalized)) {
      setState('error');
      return;
    }
    setState('saving');
    try {
      const r = await api.patch(`sales/${sale.id}/`, { price: normalized === '' ? null : normalized });
      onSaved(r.data);
      setState('saved');
      setTimeout(() => setState((st) => (st === 'saved' ? null : st)), 1500);
    } catch (err) {
      setState('error');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
    if (e.key === 'Escape') {
      cancelledRef.current = true;
      setDraft(formatPrice(sale.price));
      setState(null);
      e.target.blur();
    }
  };

  return (
    <div className="sales-price-cell">
      <input
        className={`sales-price-input${state === 'error' ? ' error' : isMissingPrice(sale) ? ' missing' : ''}`}
        inputMode="decimal"
        placeholder="0,00"
        value={draft}
        onChange={(e) => { setDraft(e.target.value.replace(/[^\d.,]/g, '')); if (state === 'error') setState(null); }}
        onBlur={save}
        onKeyDown={handleKeyDown}
        disabled={state === 'saving'}
        title={state === 'error' ? 'Precio inválido: hasta 6 enteros y 6 decimales' : 'Escriba el precio y presione Enter'}
        aria-label={`Precio 43 lbs de la venta del ${formatDate(sale.date)}`}
      />
      <span className="sales-price-state" aria-live="polite">
        {state === 'saving' ? '…' : state === 'saved' ? '✓' : state === 'error' ? '!' : ''}
      </span>
    </div>
  );
}

export default function SalesHistoryPage() {
  const { user } = useAuth();
  const canSeePrices = hasNavPermission(user, 'sale_prices');
  const canDelete = hasNavPermission(user, 'delete_records');
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [exportingId, setExportingId] = useState(null);

  const [preview, setPreview] = useState(null); // { sale } | null
  const { filtered, bar: filterBar } = useHistoryFilters(sales, FILTER_FIELDS);
  // "Sin precio" view: every sale missing a Precio 43 lbs, whatever the filters (?sinPrecio=1).
  const [searchParams, setSearchParams] = useSearchParams();
  const onlyMissing = canSeePrices && searchParams.get('sinPrecio') === '1';
  const setOnlyMissing = (on) => setSearchParams(on ? { sinPrecio: '1' } : {}, { replace: true });
  const missingSales = canSeePrices ? sales.filter(isMissingPrice) : [];
  const rows = onlyMissing ? missingSales : filtered;

  // Prices only for users who can see them (the API leaves them out for everyone else).
  const summary = salesSummary(rows, canSeePrices);

  const handleExcel = async (sale) => {
    setExportingId(sale.id);
    setStatus(null);
    try {
      await exportSaleToExcel(sale);
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo exportar a Excel.' });
    } finally {
      setExportingId(null);
    }
  };

  const handlePDF = (sale) => {
    setStatus(null);
    try {
      exportSaleToPDF(sale);
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo exportar a PDF.' });
    }
  };

  useEffect(() => {
    api.get('sales/')
      .then((r) => setSales(r.data))
      .catch(() => setStatus({ type: 'error', text: 'No se pudo cargar el historial de ventas.' }))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta venta?')) return;
    setDeletingId(id);
    setStatus(null);
    try {
      await api.delete(`sales/${id}/`);
      setSales((xs) => xs.filter((x) => x.id !== id));
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudo eliminar la venta.') });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="companies-root">
      <div className="sales-form-topbar">
        <Link to="/ventas" className="topbar-btn">← Ventas</Link>
        <Link to="/ventas/formulario" className="topbar-btn topbar-btn-primary">+ Nueva venta</Link>
      </div>
      <h1 className="companies-title">📋 Historial de Ventas</h1>
      <p className="page-subtitle page-header-divided">Una hoja por venta. Ábrela para seguir editando, o elimínala desde aquí.</p>

      {status && <div className={`companies-status companies-status-${status.type}`}>{status.text}</div>}

      {canSeePrices && (missingSales.length > 0 || onlyMissing) && (
        <div className="price-alert" role="alert">
          <span>
            {onlyMissing
              ? `Mostrando ${missingSales.length} ${missingSales.length === 1 ? 'venta' : 'ventas'} sin Precio 43 lbs (sin filtros).`
              : `⚠️ ${missingSales.length} ${missingSales.length === 1 ? 'venta no tiene' : 'ventas no tienen'} Precio 43 lbs (vacío o 0,00).`}
          </span>
          <button type="button" className="price-alert-btn" onClick={() => setOnlyMissing(!onlyMissing)}>
            {onlyMissing ? 'Ver todas' : 'Ver solo esas'}
          </button>
        </div>
      )}

      {!onlyMissing && filterBar}

      <HistorySummary cards={summary.real} />
      <HistorySummary cards={summary.boxes43} />

      <div className="companies-card">
        {loading ? (
          <div className="companies-empty">Cargando…</div>
        ) : sales.length === 0 ? (
          <div className="companies-empty">Aún no hay ventas registradas.</div>
        ) : onlyMissing && rows.length === 0 ? (
          <div className="companies-empty">✓ Todas las ventas tienen Precio 43 lbs.</div>
        ) : rows.length === 0 ? (
          <div className="companies-empty">Sin ventas para los filtros seleccionados.</div>
        ) : (
          <table className="companies-table sales-history-table">
            <thead>
              <tr>
                <th className="sales-id-col">ID de Venta</th>
                <th>Fecha</th>
                <th>Empresa</th>
                <th>Hacienda</th>
                <th>Exportadora</th>
                <th>Marca</th>
                <th>Transporte</th>
                <th className="num">Cajas</th>
                <th className="num">Pallets</th>
                <th className="num">Peso caja</th>
                {canSeePrices && <th className="num">Precio 43 lbs</th>}
                {canSeePrices && <th className="num" title="Precio 43 lbs ÷ 43 × Peso caja">Precio real</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr
                  key={s.id}
                  title={[
                    s.created_by_username && `Registrado por ${s.created_by_username}`,
                    s.comment && `Observación: ${s.comment}`,
                  ].filter(Boolean).join('\n') || undefined}
                >
                  <td className="sales-id-col sales-id">{s.id}</td>
                  <td>
                    <div>{formatDate(s.date)}{s.comment && <span className="sales-comment-flag" aria-label="Tiene observación"> 💬</span>}</div>
                    <div className="sales-sub">Sem {s.week} · {s.day}</div>
                  </td>
                  <td>{s.company_name}</td>
                  <td>
                    <div>{s.farm_name}</div>
                    <div className="sales-sub">MAGAP {s.magap || '—'}</div>
                  </td>
                  <td>{s.exporter_name}</td>
                  <td>
                    <div>{s.brand_name}</div>
                    {s.box_type_name && <div className="sales-sub">Caja {s.box_type_name}</div>}
                  </td>
                  <td>{s.transportation_name}</td>
                  <td className="num">{s.boxes.toLocaleString('es-EC')}</td>
                  <td className="num">{s.pallets}</td>
                  <td className="num">
                    <div>{Number(s.fruit_weight).toLocaleString('es-EC')}</div>
                  </td>
                  {canSeePrices && (
                    <td className="num">
                      <PriceCell
                        sale={s}
                        onSaved={(updated) => {
                          setSales((xs) => xs.map((x) => (x.id === updated.id ? updated : x)));
                          window.dispatchEvent(new Event(SALE_PRICES_CHANGED_EVENT));
                        }}
                      />
                    </td>
                  )}
                  {canSeePrices && (
                    <td className="num sales-real-price">
                      {/* Saved by the server on every save: Precio 43 lbs ÷ 43 × Peso caja (× 43 for a
                          brand with "Precio real a 43 lbs"). */}
                      {s.real_price == null
                        ? '—'
                        : Number(s.real_price).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                      {s.brand_real_price_at_43 && (
                        <span
                          className="sales-at43-badge"
                          title={`${s.brand_name}: Precio real y cajas 43lbs calculados a 43 lbs (peso guardado ${Number(s.fruit_weight).toLocaleString('es-EC')} lbs)`}
                        >
                          43
                        </span>
                      )}
                    </td>
                  )}
                  <td>
                    <div className="row-actions">
                      <Link className="row-icon-btn" to={`/ventas/formulario/${s.id}`} title="Editar" aria-label="Editar">✏️</Link>
                      <button
                        className="row-icon-btn"
                        onClick={() => setPreview({ sale: s })}
                        disabled={exportingId === s.id}
                        title="Exportar (Excel o PDF)"
                        aria-label="Exportar"
                      >
                        📤
                      </button>
                      <RowProperties item={s} title={`Venta #${s.id} · ${s.farm_name}`} />
                      {canDelete && (
                        <button
                          className="row-icon-btn"
                          onClick={() => handleDelete(s.id)}
                          disabled={deletingId === s.id}
                          title="Eliminar"
                          aria-label="Eliminar"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {preview && (
        <ExportPreviewModal
          title={`Venta #${preview.sale.id} · ${preview.sale.farm_name}`}
          fileName={salesSheet(preview.sale).fileBase}
          onClose={() => setPreview(null)}
          onDownload={(fmt) => (fmt === 'excel' ? handleExcel(preview.sale) : handlePDF(preview.sale))}
        >
          <SheetPreview sheet={salesSheet(preview.sale)} />
        </ExportPreviewModal>
      )}
    </div>
  );
}
